// The two SME brands, captured verbatim from rensure-sme-smp/lib/domain/brands.ts.
// Only these two exist — everything a template needs to look/sound like a brand
// (name, colours, logo, call number) comes from here.
// Redesign: colours, fonts and artwork follow the HQ Communications welcome emails.

// fixed S3 host so local and production emails load the same artwork
const ASSET_BASE = "https://assets.mindyourbusiness.co.uk";

export type BrandKey = "BUSINESS_COMPARE_DIRECT" | "MIND_YOUR_BUSINESS";

/** One web font file served from the brand's asset folder. */
export type FontFace = { file: string; format: "woff2" | "truetype"; weight: string };

export type BrandConfig = {
  key: BrandKey;
  name: string;
  shortName: string;
  fromEmail: string;
  callNumber: string;
  portalDomain: string;
  /** Solid brand colour — labels, buttons, highlights. */
  primary: string;
  /** Header / payment-card fill (solid in the redesign). */
  accentBg: string;
  /** Absolute logo URL (PNG — email clients don't render SVG reliably). */
  logoUrl?: string;
  logoAlign?: "left" | "center";
  /** Logo display width in px on the white masthead. */
  logoWidth: number;
  /** Absolute folder holding this brand's email artwork and fonts. */
  assetBase: string;
  /** Headings and key figures. */
  ink: string;
  /** Body copy. */
  text: string;
  /** Pale brand fill for panels and bands. */
  soft: string;
  /** Hairline borders on cards and rows. */
  line: string;
  /** The grey page behind the email. */
  page: string;
  /** Web font family name, with safe fallbacks appended by the layout. */
  font: string;
  fontFaces: FontFace[];
  /** Hero artwork; `dark` puts white copy on a navy fill (BC) instead of ink over a light scene (MYB). */
  hero: { image: string; dark: boolean; fill: string };
  /** Two-line closing sign-off, second line in the brand colour. */
  closing: [string, string];
};

export const BRANDS: Record<BrandKey, BrandConfig> = {
  BUSINESS_COMPARE_DIRECT: {
    key: "BUSINESS_COMPARE_DIRECT",
    name: "BusinessCompare Direct",
    shortName: "BusinessCompare",
    fromEmail: "renewals@businesscomparedirect.co.uk",
    callNumber: "0333 202 6242",
    portalDomain: "myportal.co.uk",
    primary: "#0750d9",
    accentBg: "#0750d9",
    logoUrl: `${ASSET_BASE}/email-assets/business-compare-direct/bc-official-logo.png`,
    logoAlign: "left",
    logoWidth: 235,
    assetBase: `${ASSET_BASE}/email-assets/business-compare-direct`,
    ink: "#173369",
    text: "#52677f",
    soft: "#f0f6ff",
    line: "#d6e3f7",
    page: "#edf3f8",
    font: "BrandonText",
    fontFaces: [
      { file: "brandon-regular.woff2", format: "woff2", weight: "400 500" },
      { file: "brandon-medium.woff2", format: "woff2", weight: "600" },
      { file: "brandon-bold.woff2", format: "woff2", weight: "700 900" },
    ],
    hero: { image: "bc-grid.png", dark: true, fill: "#071936" },
    closing: ["Back to business.", "We’ll help with the insurance."],
  },
  MIND_YOUR_BUSINESS: {
    key: "MIND_YOUR_BUSINESS",
    name: "MindYourBusiness",
    shortName: "MindYourBusiness",
    fromEmail: "renewals@mindyourbusiness.co.uk",
    callNumber: "0333 202 6242",
    portalDomain: "myportal.co.uk",
    primary: "#7133db",
    accentBg: "#7133db",
    logoUrl: `${ASSET_BASE}/email-assets/mind-your-business/brand-on-white.png`,
    logoAlign: "left",
    logoWidth: 208,
    assetBase: `${ASSET_BASE}/email-assets/mind-your-business`,
    ink: "#111a39",
    text: "#535c72",
    soft: "#f7f2ff",
    line: "#e6deef",
    page: "#eeedf3",
    font: "Gilroy",
    fontFaces: [
      { file: "gilroy-medium.woff2", format: "woff2", weight: "400 500" },
      { file: "gilroy-semibold.ttf", format: "truetype", weight: "600" },
      { file: "gilroy-bold.woff2", format: "woff2", weight: "700 900" },
    ],
    hero: { image: "myb-street-3d-shields-20261018.jpg", dark: false, fill: "#ffffff" },
    closing: ["Mind your business.", "We’ll help with the insurance."],
  },
};

export const getBrand = (key: BrandKey): BrandConfig => BRANDS[key];
